import { OrganizationUserType } from '@mfw/common';
import { useIntl } from '@mfw/components';

import {
  useOrganizationContext,
  useNavigationContext,
  useAuthContext,
  useActingUserType,
} from '../../../../context';
import { NavigationItem } from '../../../../components';
import { useShowTopHomeCalendar } from '../../../../hooks';
import useLogicalProfiles from '../../../../hooks/users/useLogicalProfiles';
// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../../../../hooks/tenants/useTenantFeatures';
import { usePremiumSubscription } from '../../../../hooks/premium';
import { useBuildRoute } from '../../../../router/hooks';
import messages from '../messages';

export interface SidebarTopItemsProps {
  onNavigate: () => void;
}

/**
 * The account-level block: no section header, always open, shown above the
 * org-scoped sections regardless of which (or whether an) organization is
 * selected.
 */
const SidebarTopItems = ({ onNavigate }: SidebarTopItemsProps) => {
  const { currentUser } = useAuthContext();
  const { isPersonalMode } = useOrganizationContext();
  const { isMiniBarAvailable } = useNavigationContext();
  const { formatMessage } = useIntl();
  const { of } = useBuildRoute();
  const showTopHomeCalendar = useShowTopHomeCalendar();
  const { isOn } = useTenantFeatures();
  const isMarketplaceOn = isOn('marketplace');

  // The tenant's premium switch hides the row of an account with nothing
  // left in it, but not of one that keeps catalog access through its
  // subscription (the server's entitlement verdict, `access`): that account's
  // catalog and subscription management stay behind this row. Asked only when
  // the row would otherwise show and the switch is off.
  const isPremiumRowCandidate = isPersonalMode && !currentUser?.isPlatformUser;
  const isPremiumOn = isOn('premium');
  const { data: premiumStatus } = usePremiumSubscription({
    enabled: isPremiumRowCandidate && !isPremiumOn,
    silent: true,
  });
  const showPremiumRow =
    isPremiumRowCandidate && (isPremiumOn || Boolean(premiumStatus?.access));

  // "My calendar" and "Fitness stats" are personal/consumer rows: visible in
  // the org-less personal profile, and to a CLIENT membership inside an org
  // (their own booked sessions/stats). Hidden for every staff-side role
  // (OWNER/TRAINER/STAFF) in every organization, EXCEPT that "My calendar"
  // comes back for an OWNER/TRAINER once they hold a second B2B membership
  // (see `showMyCalendarForMultiOrg` below) — Fitness Stats has no such
  // exception.
  const actingUserType = useActingUserType();
  const showPersonalConsumerItems =
    isPersonalMode || actingUserType === OrganizationUserType.CLIENT;
  // STAFF gets a work-only sidebar — the consumer items (personal calendar,
  // marketplace, saved, fitness stats) have no staff-facing purpose and stay
  // hidden, same reasoning as the platform-user carve-out below but for a
  // different actor class.
  const isStaff = actingUserType === OrganizationUserType.STAFF;
  const isClient = actingUserType === OrganizationUserType.CLIENT;
  // OWNER/TRAINER acting inside an organization: Marketplace and Saved don't
  // disappear for them, they move — Marketplace to the bottom of the org
  // sections and Saved into the Library section (see `SidebarContent` and
  // `useOrgSidebarSections`) — so both rows are excluded from this
  // account-level block for exactly this group, not hidden outright.
  const isOwnerOrTrainerInOrg =
    !isPersonalMode && !currentUser?.isPlatformUser && !isStaff && !isClient;

  const logicalProfiles = useLogicalProfiles(!currentUser?.isPlatformUser);
  const b2bProfileCount = logicalProfiles.data?.filter(
    (profile) => profile.context === 'B2B',
  ).length;
  // 2+ organizations: the top row becomes the cross-org "My Home".
  const isMultiOrg = b2bProfileCount !== undefined && b2bProfileCount >= 2;
  // Once an owner/trainer holds a second B2B membership, no single org's
  // "<Org> Schedule" covers their whole calendar any more, so the personal
  // aggregate view earns a place here too — unlike Fitness Stats, which stays
  // a CLIENT/personal-only row regardless of membership count.
  const showMyCalendarForMultiOrg =
    isOwnerOrTrainerInOrg &&
    b2bProfileCount !== undefined &&
    b2bProfileCount >= 2;

  return (
    <>
      {!currentUser?.isPlatformUser && showTopHomeCalendar && (
        <NavigationItem
          label={formatMessage(isMultiOrg ? messages.myHome : messages.home)}
          onClick={onNavigate}
          data-testid="sidebar-home"
          mini={isMiniBarAvailable}
          icon="mdi:house"
          to={of('/')}
        />
      )}
      {/* F4 (platform-user-class, P6): platform users get a management-first
          sidebar — Home, the consumer items (personal Schedule/Saved/Stats and
          the Wallet: a platform account never owns cards) and Marketplace
          disappear; Premium stays as the curation workspace, and the org
          section below stays untouched for the drill-down. */}
      {!currentUser?.isPlatformUser &&
        (showPersonalConsumerItems || showMyCalendarForMultiOrg) && (
          <NavigationItem
            label={formatMessage(messages.myCalendar)}
            onClick={onNavigate}
            data-testid="sidebar-schedule"
            mini={isMiniBarAvailable}
            icon="mdi:calendar-account"
            to={of('/schedule')}
          />
        )}
      {/* OWNER/TRAINER: Marketplace moves to the bottom of the org sections
          (`SidebarContent`) instead of living up here. */}
      {!currentUser?.isPlatformUser &&
        !isStaff &&
        !isOwnerOrTrainerInOrg &&
        isMarketplaceOn && (
          <NavigationItem
            label={formatMessage(messages.marketplace)}
            onClick={onNavigate}
            data-testid="sidebar-marketplace"
            icon="mdi:storefront-outline"
            mini={isMiniBarAvailable}
            to={of('/marketplace')}
          />
        )}
      {/* OWNER/TRAINER: Saved moves into the Library section instead
          (`useOrgSidebarSections`). */}
      {!currentUser?.isPlatformUser && !isStaff && !isOwnerOrTrainerInOrg && (
        <NavigationItem
          label={formatMessage(messages.saved)}
          onClick={onNavigate}
          icon="mdi:bookmark-outline"
          data-testid="sidebar-saved"
          mini={isMiniBarAvailable}
          to={of('/saved')}
        />
      )}
      {!currentUser?.isPlatformUser &&
        !isStaff &&
        showPersonalConsumerItems && (
          <NavigationItem
            label={formatMessage(messages.statistics)}
            onClick={onNavigate}
            data-testid="sidebar-stats"
            mini={isMiniBarAvailable}
            icon="mdi:chart-line"
            to={of('/stats')}
          />
        )}
      {/* Premium browsing is scoped to the org-less personal profile only. It
          is hidden whenever an organization is selected (and in corp mode via
          SidebarContent's early return). Platform users author premium content
          from the Library builders (premium switch), so they get no row. With
          the tenant's premium switch off the row stays only for an account
          that keeps catalog access. */}
      {showPremiumRow && (
        <NavigationItem
          label={formatMessage(messages.premium)}
          onClick={onNavigate}
          data-testid="sidebar-premium"
          mini={isMiniBarAvailable}
          icon="mdi:crown-outline"
          to={of('/premium')}
        />
      )}
    </>
  );
};

export default SidebarTopItems;
